import PropTypes from "prop-types";
import { matchPath, useLocation, Link } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { Tooltip } from "@mui/material";

// project-imports
import useConfig from "hooks/useConfig";
import { MenuOrientation } from "config";
import { handlerDrawerOpen, useGetMenuMaster } from "api/menu";
import {
  V2,
  navItemSx,
  miniIconSx,
  iconFilterAccent,
  iconFilterMuted,
  iconFilterOnAccent,
} from "../../drawerV2Tokens";

// ==============================|| NAVIGATION - ITEM ||============================== //

export default function NavItem({
  item,
  level,
  isParents = false,
}) {
  const theme = useTheme();
  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { menuOrientation } = useConfig();

  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      variant="liner"
      size={22}
      style={{
        ...(menuOrientation === MenuOrientation.HORIZONTAL &&
          isParents && { fontSize: 20, stroke: "1" }),
      }}
    />
  ) : (
    false
  );

  const { pathname } = useLocation();
  const isSelected = !!matchPath(
    { path: item?.link ? item.link : item.url, end: false },
    pathname,
  );

  const isExternalUrl =
    item.url &&
    (item.url.startsWith("http://") || item.url.startsWith("https://"));

  const handleExternalClick = (e) => {
    if (isExternalUrl) {
      e.preventDefault();
      window.location.href = item.url;
    }
    if (downLG) {
      handlerDrawerOpen(false);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      <ListItemButton
        component={isExternalUrl ? "div" : Link}
        to={isExternalUrl ? undefined : item.url}
        href={isExternalUrl ? item.url : undefined}
        target={itemTarget}
        disabled={item.disabled}
        selected={isSelected}
        onClick={
          isExternalUrl
            ? handleExternalClick
            : downLG
              ? () => handlerDrawerOpen(false)
              : undefined
        }
        sx={navItemSx({ drawerOpen, level, isSelected })}
      >
        {itemIcon && drawerOpen && (
          <ListItemIcon
            sx={{
              position: "relative",
              minWidth: 38,
              filter: isSelected ? iconFilterOnAccent : iconFilterMuted,
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}

        {itemIcon && !drawerOpen && (
          <Tooltip title={item.title} placement="right" arrow>
            <ListItemIcon sx={miniIconSx({ isSelected })}>
              <Box component="span" className="drawer-v2-mini-icon">
                {itemIcon}
              </Box>
            </ListItemIcon>
          </Tooltip>
        )}

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Box
                sx={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: isSelected ? V2.onAccent : V2.ink,
                    fontWeight: isSelected ? 600 : 500,
                    fontFamily: V2.fontBody,
                    fontSize: "0.9rem",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
            }
          />
        )}
      </ListItemButton>
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
};
